'use client';

import { defaultLocale, locales } from '@eos/shared-locale-utility/locale';
import { useSearchParams } from 'next/navigation';
// eslint-disable-next-line no-restricted-imports
import NextLink, { LinkProps as NextLinkProps } from 'next/link';
import { FC } from 'react';
import { useIntl } from 'react-intl';
import { UrlObject } from 'url';

const isUrlStringAbsolute = (url: string | UrlObject) => {
  // If the URL is a string, check if it has a protocol or starts with '//'
  if (typeof url === 'string') {
    return url.indexOf('://') > 0 || url.startsWith('//');
  }
  // Otherwise the url is a UrlObject which is always absolute
  return true;
};

export type LinkProps = Omit<
  React.AnchorHTMLAttributes<HTMLAnchorElement>,
  keyof NextLinkProps
> &
  NextLinkProps;

/**
 * A wrapper around Next.js's `Link` component that automatically persists the `aid` and `aid_label` search parameters from the current page when navigating.
 * If the `aid` and `aid_label` are present the destination URL, they will not be updated even if they are different from the current page.
 *
 * @example
 * ```tsx
 * <Link href="/dashboard">Dashboard</Link>
 * ```
 *
 * Read more: [Next.js Docs: `Link`](https://nextjs.org/docs/api-reference/next/link)
 */
export const Link: FC<LinkProps> = ({ href, ...props }) => {
  const searchParams = useSearchParams();
  const { locale } = useIntl();

  // Create a new URL object from the href which can either be a string (relative URL) or a URL object
  let newUrl: URL;
  if (typeof href === 'string') {
    newUrl = new URL(href, 'http://localhost');
  } else {
    newUrl = new URL(href.toString());
  }

  // Add the aid and aid_label search params if they are not already present
  if (
    !newUrl.searchParams.has('aid') &&
    !newUrl.searchParams.has('aid_label') &&
    searchParams?.has('aid') &&
    searchParams?.has('aid_label')
  ) {
    newUrl.searchParams.set('aid', searchParams?.get('aid') as string);
    newUrl.searchParams.set(
      'aid_label',
      searchParams?.get('aid_label') as string
    );
  }

  const relativeUrl = newUrl.pathname;
  const currentLocale = locale || defaultLocale;
  const startsWithDefinedLocale = locales.some((locale) =>
    relativeUrl.startsWith(`/${locale}/`)
  );

  const relativeUrlWithLocale = startsWithDefinedLocale
    ? relativeUrl
    : `/${currentLocale}${relativeUrl}`;

  newUrl.pathname = relativeUrlWithLocale;

  // If the URL is absolute, use the NextLink component as is
  if (isUrlStringAbsolute(href)) {
    return <NextLink href={newUrl} {...props} />;
  } // The URL is relative, check if the search params are empty
  else if (newUrl.searchParams.toString() === '') {
    // If the search params are empty, only use the pathname and hash
    return <NextLink href={newUrl.pathname + newUrl.hash} {...props} />;
  } else {
    // Otherwise create a new relative url using the pathname and search params
    const newHref =
      newUrl.pathname + '?' + newUrl.searchParams.toString() + newUrl.hash;
    return <NextLink href={newHref} {...props} />;
  }
};
